import { ReactNode, useEffect, useState } from 'react';

import { ThemeProvider } from '@displate/styleguide';
import theme from '@displate/styleguide/dist/theme/displate/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dynamic from 'next/dynamic';
import Script from 'next/script';

import { FacebookPixel } from '@/common/analytics/FacebookPixel';
import ErrorBoundary from '@/common/components/ErrorBoundry/ErrorBoundary';
import { ConfigPublic } from '@/common/config.public';
import { APP_VERSION_HEADER_NAME } from '@/common/constants/httpHeaders';
import { REACT_QUERY_DEFAULT_OPTIONS } from '@/common/constants/reactQuery';
import { ModalContextProvider } from '@/common/contexts/ModalContext/ModalContext';
import { PromoBarProvider } from '@/common/contexts/PromoBarContext/PromoBarContext';
import { ToastContextProvider } from '@/common/contexts/ToastContext/ToastContext';
import { useArtQuery } from '@/common/hooks/useMerchantId/useArtQuery';
import { CriticalIconsSprite } from '@/common/icons/CriticalIcons/CriticalIconsSprite';
import ComponentWithSessionIframe from '@/common/layouts/SessionIframe/SessionIframe';
import DynamicGitInfo, { GitInfo } from '@/common/utils/git/GitInfo';
import { cleanStorageFromOldData } from '@/common/utils/localStorageCleaner';
import { AuthProvider } from '@/contexts/auth/AuthContext';
import { ComingSoonDrawerContextProvider } from '@/modules/HomePage/components/ComingSoon/ComingSoonDrawer/ComingSoonDrawerContext';
import { GlobalStyles } from '@/styles/GlobalStyles';
import { OpenAPI } from 'generatedBfw/static';

interface DefaultLayoutStackProps {
  children: ReactNode;
  gitInfo?: GitInfo | null;
}

const CookieBar = dynamic(() => import('@/common/components/CookieBar/CookieBar'), {
  ssr: false,
});

OpenAPI.BASE = '';
OpenAPI.HEADERS = {
  [APP_VERSION_HEADER_NAME]: ConfigPublic.BuildVersion,
  'App-Env': 'web',
};

const DefaultLayoutStack = ({ gitInfo, children }: DefaultLayoutStackProps) => {
  const [queryClient] = useState(() => new QueryClient(REACT_QUERY_DEFAULT_OPTIONS));
  useArtQuery();

  useEffect(() => {
    cleanStorageFromOldData();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CriticalIconsSprite />
          <PromoBarProvider>
            <ErrorBoundary>
              <GlobalStyles />
              <ComponentWithSessionIframe />
              {/* comingsoon context is required here so that buttons from modal context are able to access it */}
              <ComingSoonDrawerContextProvider>
                <ModalContextProvider>
                  <ToastContextProvider>{children}</ToastContextProvider>
                </ModalContextProvider>
              </ComingSoonDrawerContextProvider>
              <FacebookPixel />
              <CookieBar />
            </ErrorBoundary>
          </PromoBarProvider>
        </ThemeProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      {gitInfo && <DynamicGitInfo git={gitInfo} />}
      <Script src={ConfigPublic.AthenaScriptUrl} strategy="lazyOnload" />
    </QueryClientProvider>
  );
};
export default DefaultLayoutStack;
